import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, X, FileText, AlertTriangle, Lightbulb,
  TrendingUp, Loader2, ChevronRight, Globe,
} from 'lucide-react';
import useProjectSearch from '../hooks/useProjectSearch';

// ── Config groupes ────────────────────────────────────────────────────────────

const GROUPS = [
  {
    key: 'pages',
    label: 'Pages auditées',
    icon: FileText,
    color: 'text-accent',
    bg: 'bg-accent/10',
    navFn: (result, slug) => {
      // Même logique que ProjectSEO : chemin avec ___ comme séparateur
      let pageSlug = 'home';
      try {
        const u = new URL(result.url);
        const path = u.pathname.replace(/^\/+|\/+$/g, '');
        pageSlug = path ? path.replace(/\//g, '___') : 'home';
      } catch {
        pageSlug = encodeURIComponent(result.url);
      }
      return `/projects/${slug}/seo/${pageSlug}`;
    },
  },
  {
    key: 'errors',
    label: 'Points à corriger',
    icon: AlertTriangle,
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    navFn: (_result, slug) => `/projects/${slug}/errors`,
  },
  {
    key: 'recommendations',
    label: 'Recommandations IA',
    icon: Lightbulb,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    navFn: (_result, slug) => `/projects/${slug}/position`,
  },
  {
    key: 'positioning',
    label: 'Positionnement',
    icon: TrendingUp,
    color: 'text-green-700',
    bg: 'bg-green-50',
    navFn: (_result, slug) => `/projects/${slug}/position`,
  },
  {
    key: 'siteInfo',
    label: 'Infos du site',
    icon: Globe,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    navFn: (_result, slug) => `/projects/${slug}`,
  },
];

const SUGGESTIONS = ['WordPress', 'CMS', 'meta description', 'backlink', 'concurrent', 'performance', 'erreur'];

// ── Highlight ────────────────────────────────────────────────────────────────

function Highlight({ text, query }) {
  if (!query || !text) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-accent/25 text-blue-gray-900 rounded-sm not-italic font-medium">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function AuditSearchBar({ slug, project = null, className = '' }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const { load, search, loading, ready } = useProjectSearch(slug, project);

  // On first focus: start loading data
  const handleFocus = useCallback(() => {
    setFocused(true);
    if (!ready && !loading) load();
  }, [ready, loading, load]);

  // Click outside → close dropdown
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current?.contains(e.target)) setFocused(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Flatten search results for display
  const { flat, resultItems } = useMemo(() => {
    if (!query.trim()) return { flat: [], resultItems: [] };
    const raw = search(query);
    if (!raw) return { flat: [], resultItems: [] };
    const flat = [];
    const resultItems = [];
    for (const group of GROUPS) {
      const items = (raw[group.key] || []).slice(0, 8);
      if (!items.length) continue;
      flat.push({ type: 'header', group });
      for (const item of items) {
        flat.push({ type: 'result', result: item, group });
        resultItems.push({ result: item, group });
      }
    }
    return { flat, resultItems };
  }, [search, query]);

  // Reset active index when results change
  useEffect(() => setActiveIndex(0), [query]);

  const handleSelect = useCallback(
    (result, group) => {
      navigate(group.navFn(result, slug), {
        state: {
          searchHighlight: query,
          // Pour la page de positionnement : quel sous-groupe a matché
          posKey: result.posKey || null,
        },
      });
      setFocused(false);
      setQuery('');
    },
    [navigate, slug, query]
  );

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!focused) return;
    if (e.key === 'Escape') { setFocused(false); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, resultItems.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    }
    if (e.key === 'Enter' && resultItems[activeIndex]) {
      handleSelect(resultItems[activeIndex].result, resultItems[activeIndex].group);
    }
  };

  const showDropdown = focused;
  const totalCount = resultItems.length;

  let itemCounter = -1;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input */}
      <div className="relative">
        {loading && !ready ? (
          <Loader2 size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-gray-300 animate-spin pointer-events-none" />
        ) : (
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-gray-400 pointer-events-none" />
        )}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder="Chercher dans tout l'audit : URL, CMS, erreur, mot-clé…"
          className={`w-full pl-10 pr-9 py-2.5 text-sm bg-white border rounded-lg outline-none transition
            ${focused
              ? 'border-accent/50 ring-2 ring-accent/15 shadow-sm'
              : 'border-blue-gray-200 hover:border-blue-gray-300'
            } placeholder:text-blue-gray-300 text-blue-gray-700`}
        />
        {query && (
          <button
            onMouseDown={(e) => { e.preventDefault(); setQuery(''); inputRef.current?.focus(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-gray-300 hover:text-blue-gray-500 transition"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1.5 bg-white rounded-xl shadow-2xl border border-blue-gray-100 overflow-hidden"
          style={{ maxHeight: '420px', overflowY: 'auto' }}>

          {/* Loading state */}
          {loading && !ready && (
            <div className="px-4 py-5 text-center">
              <Loader2 size={18} className="text-blue-gray-300 mx-auto mb-2 animate-spin" />
              <p className="text-xs text-blue-gray-400">Chargement des données de l'audit…</p>
            </div>
          )}

          {/* Idle state — ready but no query */}
          {ready && !query && (
            <div className="px-4 py-4">
              <p className="text-xs text-blue-gray-400 mb-3">Suggestions :</p>
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onMouseDown={(e) => { e.preventDefault(); setQuery(s); inputRef.current?.focus(); }}
                    className="text-xs px-2.5 py-1 rounded-full bg-blue-gray-50 text-blue-gray-500 border border-blue-gray-200 hover:bg-accent/10 hover:text-accent hover:border-accent/30 transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* No results */}
          {ready && query && totalCount === 0 && (
            <div className="px-4 py-5 text-center">
              <p className="text-sm text-blue-gray-500">Aucun résultat pour « {query} »</p>
              <p className="text-xs text-blue-gray-300 mt-1">Essayez un autre terme.</p>
            </div>
          )}

          {/* Results */}
          {flat.map((row, i) => {
            if (row.type === 'header') {
              const Icon = row.group.icon;
              return (
                <div key={`h-${i}`} className="flex items-center gap-2 px-4 pt-3 pb-1">
                  <Icon size={11} className={row.group.color} />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-gray-400">
                    {row.group.label}
                  </span>
                </div>
              );
            }
            itemCounter++;
            const myIndex = itemCounter;
            const isActive = activeIndex === myIndex;
            return (
              <button
                key={`r-${i}`}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(row.result, row.group); }}
                className={`w-full flex items-start gap-2.5 px-4 py-2.5 text-left transition-colors
                  ${isActive ? 'bg-accent/8 border-l-2 border-accent' : 'border-l-2 border-transparent hover:bg-blue-gray-50/70'}`}
              >
                <span className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${row.group.bg}`}>
                  {React.createElement(row.group.icon, { size: 11, className: row.group.color })}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-blue-gray-800 truncate leading-snug font-medium">
                    <Highlight text={row.result.title} query={query} />
                  </p>
                  {(row.result.subtitle || row.result.url) && (
                    <p className="text-xs text-blue-gray-400 truncate mt-0.5">
                      <Highlight text={row.result.subtitle || row.result.url} query={query} />
                    </p>
                  )}
                </div>
                <ChevronRight size={12} className="text-blue-gray-300 flex-shrink-0 mt-1" />
              </button>
            );
          })}

          {/* Footer count */}
          {ready && totalCount > 0 && (
            <div className="border-t border-blue-gray-50 px-4 py-2 flex items-center justify-between">
              <span className="text-[11px] text-blue-gray-400">
                {totalCount} résultat{totalCount > 1 ? 's' : ''}
              </span>
              <span className="text-[10px] text-blue-gray-300 hidden sm:block">
                <kbd className="font-sans border border-blue-gray-200 rounded px-1">↑↓</kbd> naviguer
                {' · '}
                <kbd className="font-sans border border-blue-gray-200 rounded px-1">↵</kbd> ouvrir
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
